import React, { useEffect, useState } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { Building2, Home, TrendingUp, Clock, Megaphone, Zap, Shield, Plus, Wrench, Users, BarChart3, PieChart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { LandlordStatCard } from '@/components/cards/LandlordStatCard';
import { LandlordPropertyCard } from '@/components/cards/LandlordPropertyCard';
import { LandlordTutorial, useLandlordTutorial } from '@/components/landlord/LandlordTutorial';
import { useToast } from '@/contexts/ToastContext';

export const LandlordDashboard: React.FC = () => {
    const navigate = useNavigate();
    const {
        properties,
        invoices,
        fetchAllLandlordData
    } = useLandlordStore();
    const { isDone, markDone } = useLandlordTutorial();
    const [showTutorial, setShowTutorial] = useState(false);
    const { info, success, warning } = useToast();

    useEffect(() => {
        fetchAllLandlordData().then(() => {
            const state = useLandlordStore.getState();
            if (state.properties.length === 0) {
                info('Welcome to your Landlord Portal', 'Start by adding your first property.');
            } else {
                const occupied = state.properties.reduce((a, p) => a + (p.unit_stats?.occupied || 0), 0);
                const total = state.properties.reduce((a, p) => a + (p.unit_stats?.total || 0), 0);
                const rate = total > 0 ? Math.round((occupied / total) * 100) : 0;
                if (rate < 50 && total > 0) {
                    warning('Low Occupancy', `Your portfolio is ${rate}% occupied. Consider posting vacancies.`);
                } else if (state.properties.length > 0) {
                    success('Dashboard loaded', `${state.properties.length} propert${state.properties.length === 1 ? 'y' : 'ies'} found.`);
                }
            }
        });
        if (!isDone()) {
            const t = setTimeout(() => setShowTutorial(true), 600);
            return () => clearTimeout(t);
        }
    }, []);

    const totalProperties = properties.length;
    // Calculate total units and occupancy across all properties
    // If we have units fetched separately, use them. Otherwise relying on property.unit_stats is safer if units aren't auto-fetched.
    // fetchAllLandlordData fetches properties, tenants, invoices, maintenance. 
    // It does NOT fetch all units for all properties by default (expensive). 
    // Let's rely on property.unit_stats which usually comes with property summary.

    const totalUnits = properties.reduce((acc, prop) => acc + (prop.unit_stats?.total || 0), 0);
    const occupiedUnits = properties.reduce((acc, prop) => acc + (prop.unit_stats?.occupied || 0), 0);
    const occupancyRate = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;

    // Financial Data from Invoices
    const currentMonthRevenue = invoices
        .filter(inv => {
            const date = new Date(inv.created_at || inv.due_date);
            const now = new Date();
            return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        })
        .reduce((acc, inv) => acc + (inv.amount_paid || 0), 0);

    // Sort bookings by date
    const recentBookings = [...(useLandlordStore.getState().bookings || [])]
        .sort((a, b) => new Date(b.requestedDate).getTime() - new Date(a.requestedDate).getTime())
        .slice(0, 3);

    // Sort transactions (invoices) by date
    const recentTransactions = [...invoices]
        .sort((a, b) => new Date(b.due_date).getTime() - new Date(a.due_date).getTime())
        .slice(0, 5);

    // Calculate and format real monthly data for the chart from existing invoices
    const chartData = invoices.reduce((acc: any[], inv) => {
        if (!inv.due_date) return acc;

        const date = new Date(inv.due_date);
        if (isNaN(date.getTime())) return acc;

        const month = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
        const existing = acc.find(item => item.month === month);

        const paid = inv.amount_paid || 0;
        const unpaid = inv.balance || 0;
        const expected = inv.amount || 0;

        if (existing) {
            existing.Paid += paid;
            existing.Unpaid += unpaid;
            existing.Expected += expected;
        } else {
            acc.push({
                month,
                Paid: paid,
                Unpaid: unpaid,
                Expected: expected,
                timestamp: date.getTime()
            });
        }
        return acc;
    }, []).sort((a, b) => a.timestamp - b.timestamp);

    return (
        <div className="space-y-8">
            {showTutorial && (
                <LandlordTutorial onClose={() => { setShowTutorial(false); markDone(); }} />
            )}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">Landlord Dashboard</h1>
                    <p className="text-light-subtle dark:text-dark-subtle">Overview of your real estate portfolio</p>
                </div>
            </div>

            {/* Quick Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <LandlordStatCard
                    title="Total Properties"
                    value={totalProperties}
                    icon={Building2}
                    onClick={() => navigate('/landlord/properties')}
                />
                <LandlordStatCard
                    title="Units"
                    value={totalUnits}
                    subtitle={`${occupancyRate}% occupancy`}
                    icon={Home}
                    onClick={() => navigate('/landlord/units')}
                />
                <LandlordStatCard
                    title="Revenue (This Month)"
                    value={`KES ${currentMonthRevenue.toLocaleString()}`}
                    icon={TrendingUp}
                    onClick={() => navigate('/landlord/financials')}
                />
            </div>

            {/* Properties List (Hub-like) */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-semibold text-light-text dark:text-dark-text">Your Properties</h2>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Tap a property to open the property hub and manage units, tenants, bookings and more.</p>
                </div>
                <button
                    onClick={() => navigate('/landlord/properties/new')}
                    className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
                >
                    <Plus size={18} />
                    Add Property
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.length === 0 ? (
                    <div className="col-span-full py-12 text-center bg-light-card dark:bg-dark-card rounded-xl border border-dashed border-light-border dark:border-dark-border">
                        <p className="text-lg font-medium text-light-text dark:text-dark-text">No properties yet</p>
                        <p className="text-sm text-light-subtle dark:text-dark-subtle mt-2">Start by adding your first property using the button above.</p>
                    </div>
                ) : (
                    properties.map(property => (
                        <LandlordPropertyCard
                            key={property.id}
                            property={property}
                            onClick={() => navigate(`/landlord/properties/${property.id}`)}
                        />
                    ))
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Financial Summary Chart */}
                <div className="lg:col-span-2 bg-light-card dark:bg-dark-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text dark:text-white">Monthly Revenue Overview</h3>
                        <button onClick={() => navigate('/landlord/financials')} className="text-sm text-blue-600 hover:underline">View Finance Details</button>
                    </div>
                    <div className="h-72 w-full">
                        {chartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} margin={{ top: 20, right: 0, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" className="dark:stroke-gray-700" />
                                    <XAxis
                                        dataKey="month"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#6B7280', fontSize: 12 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#6B7280', fontSize: 12 }}
                                        tickFormatter={(value) => `KES ${value.toLocaleString()}`}
                                        width={80}
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(107, 114, 128, 0.05)' }}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                        formatter={(value: any) => [`KES ${Number(value || 0).toLocaleString()}`, '']}
                                    />
                                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                    <Bar dataKey="Expected" fill="#9CA3AF" name="Expected" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                    <Bar dataKey="Paid" fill="#10B981" name="Paid" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                    <Bar dataKey="Unpaid" fill="#EF4444" name="Unpaid" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-light-subtle dark:text-dark-subtle">
                                <TrendingUp className="h-12 w-12 text-gray-300 mb-2" />
                                <p>No financial data available for chart</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Recent Bookings (Moved alongside Recent Activity) */}
                <motion.div
                    whileHover={{ y: -2 }}
                    onClick={() => navigate('/landlord/bookings')}
                    className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer group transition-shadow hover:shadow-md"
                >
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text dark:text-white">Recent Viewings</h3>
                        <span className="text-sm text-blue-600 group-hover:underline">View All</span>
                    </div>
                    {recentBookings.length === 0 ? (
                        <p className="text-light-subtle dark:text-dark-subtle text-center py-4">No recent bookings.</p>
                    ) : (
                        <div className="space-y-4">
                            {recentBookings.map((booking: any) => (
                                <div key={booking.id} className="flex justify-between items-center p-3 hover:bg-light-bg dark:bg-dark-bg dark:hover:bg-gray-100 dark:bg-gray-800 rounded-lg border border-transparent hover:border-light-border dark:border-dark-border transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-50 text-blue-600 rounded-full">
                                            <Clock size={16} />
                                        </div>
                                        <div>
                                            <p className="font-medium">{booking.tenantName || 'Visitor'}</p>
                                            <p className="text-xs text-light-subtle dark:text-dark-subtle">
                                                {new Date(booking.requestedDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${booking.status === 'Confirmed' ? 'bg-green-100 text-green-700' :
                                        booking.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 text-gray-700'
                                        }`}>
                                        {booking.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </motion.div>
            </div>

            {/* Recent Transactions Table */}
            <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card rounded-xl shadow-sm border border-light-border dark:border-dark-border p-6">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-bold">Recent Transactions</h3>
                    <button onClick={() => navigate('/landlord/financials')} className="text-sm text-blue-600 hover:underline">View All</button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="text-left text-sm text-light-subtle dark:text-dark-subtle border-b border-light-border dark:border-dark-border dark:border-gray-800">
                                <th className="pb-3 font-medium">Description</th>
                                <th className="pb-3 font-medium">Date</th>
                                <th className="pb-3 font-medium">Status</th>
                                <th className="pb-3 font-medium text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
                            {recentTransactions.map((t) => (
                                <tr key={t.id} className="group hover:bg-light-bg dark:bg-dark-bg dark:hover:bg-gray-100 dark:bg-gray-800/50 transition-colors">
                                    <td className="py-3 text-sm">
                                        <div className="font-medium text-light-text dark:text-dark-text dark:text-gray-100">Rent - {t.unit_number}</div>
                                        <div className="text-xs text-light-subtle dark:text-dark-subtle">{t.tenant_name}</div>
                                    </td>
                                    <td className="py-3 text-sm text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle">
                                        {new Date(t.due_date).toLocaleDateString()}
                                    </td>
                                    <td className="py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.status === 'Paid' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                            t.status === 'Pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                            }`}>
                                            {t.status}
                                        </span>
                                    </td>
                                    <td className="py-3 text-sm font-medium text-right text-light-text dark:text-dark-text dark:text-gray-100">
                                        KES {t.amount.toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                            {recentTransactions.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="py-4 text-center text-sm text-light-subtle dark:text-dark-subtle">No recent transactions</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Analytics & Reports Section */}
            <div>
                <h2 className="text-lg font-bold text-light-text dark:text-dark-text dark:text-white mb-4">Analytics & Reports</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <motion.div
                        whileHover={{ y: -5 }}
                        onClick={() => navigate('/landlord/reports/performance')}
                        className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer flex flex-col items-center justify-center gap-3 text-center"
                    >
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-green-600 dark:text-green-400">
                            <BarChart3 size={24} />
                        </div>
                        <span className="font-medium text-light-text dark:text-dark-text dark:text-white text-sm">Performance</span>
                    </motion.div>

                    <motion.div
                        whileHover={{ y: -5 }}
                        onClick={() => navigate('/landlord/reports/occupancy')}
                        className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer flex flex-col items-center justify-center gap-3 text-center"
                    >
                        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-purple-600 dark:text-purple-400">
                            <PieChart size={24} />
                        </div>
                        <span className="font-medium text-light-text dark:text-dark-text dark:text-white text-sm">Occupancy</span>
                    </motion.div>

                    <motion.div
                        whileHover={{ y: -5 }}
                        onClick={() => navigate('/landlord/staff/property-management')}
                        className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer flex flex-col items-center justify-center gap-3 text-center"
                    >
                        <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-teal-600 dark:text-teal-400">
                            <Users size={24} />
                        </div>
                        <span className="font-medium text-light-text dark:text-dark-text dark:text-white text-sm">Property Staff</span>
                    </motion.div>

                    <motion.div
                        whileHover={{ y: -5 }}
                        onClick={() => navigate('/landlord/reports')}
                        className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer flex flex-col items-center justify-center gap-3 text-center"
                    >
                        <div className="p-3 bg-gray-50 dark:bg-gray-900/20 rounded-lg text-gray-600 dark:text-gray-400">
                            <TrendingUp size={24} />
                        </div>
                        <span className="font-medium text-light-text dark:text-dark-text dark:text-white text-sm">All Reports</span>
                    </motion.div>
                </div>
            </div>

            {/* Quick Actions Grid */}
            <div>
                <h2 className="text-lg font-bold text-light-text dark:text-dark-text dark:text-white mb-4">Quick Actions</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    <motion.div
                        whileHover={{ y: -5 }}
                        onClick={() => navigate('/landlord/bookings')}
                        className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer flex flex-col items-center justify-center gap-3 text-center"
                    >
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-orange-600 dark:text-orange-400">
                            <Clock size={24} />
                        </div>
                        <span className="font-medium text-light-text dark:text-dark-text dark:text-white text-sm">Viewings</span>
                    </motion.div>

                    <motion.div
                        whileHover={{ y: -5 }}
                        onClick={() => navigate('/landlord/maintenance')}
                        className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer flex flex-col items-center justify-center gap-3 text-center"
                    >
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-600 dark:text-red-400">
                            <Wrench size={24} />
                        </div>
                        <span className="font-medium text-light-text dark:text-dark-text dark:text-white text-sm">Maintenance</span>
                    </motion.div>

                    <motion.div
                        whileHover={{ y: -5 }}
                        onClick={() => navigate('/landlord/vacancies')}
                        className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer flex flex-col items-center justify-center gap-3 text-center"
                    >
                        <div className="p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg text-pink-600 dark:text-pink-400">
                            <Megaphone size={24} />
                        </div>
                        <span className="font-medium text-light-text dark:text-dark-text dark:text-white text-sm">Marketing</span>
                    </motion.div>

                    <motion.div
                        whileHover={{ y: -5 }}
                        onClick={() => navigate('/landlord/utilities')}
                        className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer flex flex-col items-center justify-center gap-3 text-center"
                    >
                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-yellow-600 dark:text-yellow-400">
                            <Zap size={24} />
                        </div>
                        <span className="font-medium text-light-text dark:text-dark-text dark:text-white text-sm">Utilities</span>
                    </motion.div>

                    <motion.div
                        whileHover={{ y: -5 }}
                        onClick={() => navigate('/landlord/staff')}
                        className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer flex flex-col items-center justify-center gap-3 text-center"
                    >
                        <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-indigo-600 dark:text-indigo-400">
                            <Shield size={24} />
                        </div>
                        <span className="font-medium text-light-text dark:text-dark-text dark:text-white text-sm">Staff</span>
                    </motion.div>

                    <motion.div
                        whileHover={{ y: -5 }}
                        onClick={() => navigate('/landlord/properties/new')}
                        className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border cursor-pointer flex flex-col items-center justify-center gap-3 text-center"
                    >
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400">
                            <Plus size={24} />
                        </div>
                        <span className="font-medium text-light-text dark:text-dark-text dark:text-white text-sm">Add Property</span>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};
